import TransferSummary, { TransferResult } from '@/components/TransferSummary';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
	Manager,
	useAllManagersQuery,
	useEmployeesByManagerEmail,
	useManagersByEmailQuery,
} from '@/services/actualDataService';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { CalendarIcon, History, RefreshCw, RotateCcw } from 'lucide-react';
import React, { useCallback, useState, useTransition } from 'react';
import EmployeeList from './EmployeeList';

const ChangeManager: React.FC<{ showNames?: boolean }> = ({
	showNames = false,
}: { showNames?: boolean } = {}) => {
	const queryClient = useQueryClient();
	const [currentManagerEmail, setCurrentManagerEmail] = useState('');
	const [newManagerEmail, setNewManagerEmail] = useState('');
	const [effectiveDate, setEffectiveDate] = useState<Date>();
	const [transferResults, setTransferResults] = useState<TransferResult[]>(
		[],
	);
	const [showResults, setShowResults] = useState(false);
	const [errors, setErrors] = useState<{
		currentManager?: string;
		newManager?: string;
	}>({});
	const availableManagers = useAllManagersQuery();
	const currentManagerEmployees =
		useEmployeesByManagerEmail(currentManagerEmail);

	const currentManagerByEmail = useManagersByEmailQuery(currentManagerEmail);
	const newManagerByEmail = useManagersByEmailQuery(newManagerEmail);

	const validateForm = useCallback(
		(
			currentManagerByEmail: Manager | undefined,
			newManagerByEmail: Manager | undefined,
		): boolean => {
			const newErrors: typeof errors = {};
			if (!currentManagerEmail) {
				newErrors.currentManager = 'Current manager email is required';
			} else if (!currentManagerByEmail) {
				newErrors.currentManager = 'Manager not found';
			}
			if (!newManagerEmail) {
				newErrors.newManager = 'New manager email is required';
			} else if (!newManagerByEmail) {
				newErrors.newManager = 'Manager not found';
			}
			if (
				currentManagerEmail &&
				newManagerEmail &&
				currentManagerEmail.toLowerCase() ===
					newManagerEmail.toLowerCase()
			) {
				newErrors.newManager =
					'New manager must be different from current manager';
			}
			setErrors(newErrors);
			return Object.keys(newErrors).length === 0;
		},
		[currentManagerEmail, newManagerEmail, errors],
	);

	const handleReset = () => {
		setCurrentManagerEmail('');
		setNewManagerEmail('');
		setEffectiveDate(undefined);
		setTransferResults([]);
		setShowResults(false);
		setErrors({});
		toast({
			title: 'Form Reset',
			description: 'All fields have been cleared.',
		});
	};
	const handleAuditTrail = () => {
		toast({
			title: 'Audit Trail',
			description: 'Audit trail functionality would be implemented here.',
		});
	};
	const [isPending, startTransition] = useTransition();

	const handleExecuteTransfer = async (
		currentManagerByEmail: Manager | undefined,
		newManagerByEmail: Manager | undefined,
	): Promise<void> => {
		if (!validateForm(currentManagerByEmail, newManagerByEmail)) {
			toast({
				title: 'Validation Error',
				description: 'Please fix the errors before executing transfer.',
				variant: 'destructive',
			});
			return;
		}
		// const currentManagerByEmail = useManagersByEmailQuery(currentManagerEmail);
		// const newManagerByEmail = useManagersByEmailQuery(newManagerEmail);
		if (!currentManagerByEmail || !newManagerByEmail) return;
		const executionResponse = await fetch('/api/transfer-employees', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				managerAEmail: currentManagerByEmail.email,
				managerBEmail: newManagerByEmail.email,
				transferDate: effectiveDate
					? format(effectiveDate, 'yyyy-MM-dd')
					: format(new Date(), 'yyyy-MM-dd'),
			}),
		});
		if (!executionResponse.ok) {
			const errorData = await executionResponse.json();
			toast({
				title: 'Transfer Failed',
				description: errorData.error || 'An unknown error occurred.',
				variant: 'destructive',
			});
			return;
		}
		const executionResult = await executionResponse.json();
		if (executionResult.status !== 'success') {
			toast({
				title: 'Transfer Failed',
				description:
					executionResult.message || 'An unknown error occurred.',
				variant: 'destructive',
			});
			return;
		}

		// Simulate transfer execution with some potential failures
		const okResults: TransferResult[] =
			executionResult.data.direct_reports.map(
				(rawEmployee: {
					id: string;
					full_name: string;
					email: string;
				}) => {
					return {
						employeeId: rawEmployee.id,
						employeeName: rawEmployee.full_name,
						employeeEmail: rawEmployee.email,
						success: true,
						reason: undefined,
					};
				},
			);

		const failedResults: TransferResult[] = executionResult.data.failed.map(
			(rawEmployee: {
				id: string;
				full_name: string;
				email: string;
				error?: string;
			}) => {
				return {
					employeeId: rawEmployee.id,
					employeeName: rawEmployee.full_name,
					employeeEmail: rawEmployee.email,
					success: false,
					reason: rawEmployee.error || 'Connection Error',
				};
			},
		);
		const results = [...okResults, ...failedResults];
		setTransferResults(results);
		setShowResults(true);
		const successCount = results.filter((r) => r.success).length;
		toast({
			title: 'Transfer Completed',
			description: `Successfully transferred ${successCount} of ${results.length} employees.`,
			variant:
				successCount === results.length ? 'default' : 'destructive',
		});
		queryClient.invalidateQueries({
			queryKey: ['employees'],
		});
	};

	const formatDate = (date: Date) => {
		return format(date, 'PPP');
	};
	const getEffectiveDateDisplay = () => {
		if (effectiveDate) {
			return formatDate(effectiveDate);
		}
		return formatDate(new Date());
	};
	return (
		<div className="min-h-screen bg-gray-50 p-6 py-[24px] rounded-2xl">
			<div className="max-w-7xl mx-auto">
				{/* Header Banner */}
				<div
					style={{
						backgroundColor: 'white',
						borderTopLeftRadius: '32px',
						borderTopRightRadius: '32px',
						borderBottomLeftRadius: '0px',
						borderBottomRightRadius: '0px',
					}}
					className="bg-white px-0 py-[80px]"
				>
					<div className="text-center">
						<h1
							style={{
								color: '#aa1b55',
							}}
							className="font-bold text-4xl text-[#aa1b55]"
						>
							Change Manager
						</h1>
						<p
							style={{
								color: '#aa1b55',
							}}
							className="mt-2 text-[#aa1b55]"
						>
							Transfer employees between managers
						</p>
					</div>
				</div>

				{/* Manager Selection and Transfer Settings */}
				<Card
					className="border-0 shadow-none"
					style={{
						borderBottomLeftRadius: '32px',
						borderBottomRightRadius: '32px',
					}}
				>
					<CardContent className="space-y-6 pt-6 py-[32px] px-0 my-0">
						{/* Manager Selection - Side-by-side Labels */}
						<div className="space-y-4 max-w-2xl mx-auto">
							<div className="flex items-center gap-4">
								<Label
									htmlFor="current-manager"
									className="w-32 text-right"
								>
									Current Manager
								</Label>
								<div className="flex-1">
									<Select
										value={currentManagerEmail}
										onValueChange={setCurrentManagerEmail}
									>
										<SelectTrigger
											disabled={isPending}
											className={cn(
												'rounded-xl',
												errors.currentManager
													? 'border-red-500'
													: '',
											)}
										>
											<SelectValue placeholder="Select current manager" />
										</SelectTrigger>
										<SelectContent>
											{availableManagers.map(
												(manager) => (
													<SelectItem
														key={manager.email}
														value={
															manager.email || ''
														}
													>
														{[
															showNames &&
																manager.displayName,
															manager.email,
														]
															.filter(Boolean)
															.join(' - ')}
													</SelectItem>
												),
											)}
										</SelectContent>
									</Select>
									{errors.currentManager && (
										<p className="text-sm text-red-600 mt-1">
											{errors.currentManager}
										</p>
									)}
								</div>
							</div>

							<div className="flex items-center gap-4">
								<Label
									htmlFor="new-manager"
									className="w-32 text-right"
								>
									New Manager
								</Label>
								<div className="flex-1">
									<Select
										value={newManagerEmail}
										onValueChange={setNewManagerEmail}
									>
										<SelectTrigger
											disabled={isPending}
											className={cn(
												'rounded-xl',
												errors.newManager
													? 'border-red-500'
													: '',
											)}
										>
											<SelectValue placeholder="Select new manager" />
										</SelectTrigger>
										<SelectContent>
											{availableManagers.map(
												(manager) => (
													<SelectItem
														key={manager.email}
														value={
															manager.email || ''
														}
													>
														{[
															showNames &&
																manager.displayName,
															manager.email,
														]
															.filter(Boolean)
															.join(' - ')}
													</SelectItem>
												),
											)}
										</SelectContent>
									</Select>
									{errors.newManager && (
										<p className="text-sm text-red-600 mt-1">
											{errors.newManager}
										</p>
									)}
								</div>
							</div>

							{/* Date Selection - Side-by-side Label */}
							<div className="flex items-center gap-4">
								<Label className="w-32 text-right">
									Effective Date
								</Label>
								<div className="flex-1">
									<Popover>
										<PopoverTrigger
											disabled={isPending}
											asChild
										>
											<Button
												variant="outline"
												className={cn(
													'w-full justify-start text-left font-normal rounded-full',
													!effectiveDate &&
														'text-muted-foreground',
												)}
											>
												<CalendarIcon className="mr-2 h-4 w-4" />
												{effectiveDate
													? formatDate(effectiveDate)
													: 'Select date'}
											</Button>
										</PopoverTrigger>
										<PopoverContent
											className="w-auto p-0"
											align="start"
										>
											<Calendar
												mode="single"
												selected={effectiveDate}
												onSelect={setEffectiveDate}
												initialFocus
												className="p-3 pointer-events-auto"
											/>
										</PopoverContent>
									</Popover>
								</div>
							</div>
						</div>

						{/* Action Buttons centered */}
						<div className="flex justify-center items-center pt-4">
							<div className="flex items-center gap-2">
								<Button
									onClick={() =>
										startTransition(
											async () =>
												await handleExecuteTransfer(
													currentManagerByEmail,
													newManagerByEmail,
												),
										)
									}
									style={{
										backgroundColor: '#aa1b55',
									}}
									disabled={
										isPending ||
										!currentManagerEmail ||
										!newManagerEmail
									}
									className="flex items-center gap-2 rounded-full bg-[#aa1b55]"
								>
									<RefreshCw
										className={cn(
											'h-4 w-4',
											isPending &&
												"animate-spin cursor-[url('../../public/file.svg')]",
										)}
									/>
									Execute Transfer
								</Button>

								<Button
									onClick={handleReset}
									variant="outline"
									disabled={isPending}
									className="flex items-center gap-2 rounded-full"
								>
									<RotateCcw className="h-4 w-4" />
									Reset
								</Button>

								<Button
									onClick={handleAuditTrail}
									variant="ghost"
									className="flex items-center gap-2 rounded-full text-left px-[23px] mx-0 text-slate-400 text-xs"
								>
									<History className="h-4 w-4" />
									Audit Trail
								</Button>
							</div>
						</div>
					</CardContent>
				</Card>

				<div className="space-y-6 mt-6">
					{/* Current Manager's Employees */}
					{currentManagerEmail &&
						currentManagerEmployees.length > 0 &&
						!showResults && (
							<EmployeeList
								employees={currentManagerEmployees}
								title={`Current Employees under ${currentManagerByEmail?.displayName || currentManagerEmail}`}
							/>
						)}

					{/* Transfer Results */}
					{showResults && transferResults.length > 0 && (
						<TransferSummary
							results={transferResults}
							currentManagerEmail={currentManagerEmail}
							newManagerEmail={newManagerEmail}
							effectiveDate={getEffectiveDateDisplay()}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default ChangeManager;
